/**
 * The auth context object and its types, with no dependencies.
 *
 * AuthContext.tsx (the provider) imports the secure store, analytics and the
 * session recovery chain. A provider that only needs to know WHO is signed in —
 * ProfileProvider loads the profile for that athlete — reads this module
 * instead, so it does not pull that chain into every module that imports it
 * (utils/units → ProfileContext is imported across the app and its tests, and
 * expo-secure-store does not parse under jest).
 */
import { createContext, useContext } from 'react';

export interface AuthUser {
  id: string;
  name?: string;
  email?: string;
}

export interface AuthContextType {
  user: AuthUser | null;
  login: (user: AuthUser, skipDeviceRegistration?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** The signed-in athlete's id; null while no one is signed in (or outside an AuthProvider). */
export function useAuthUserId(): string | null {
  return useContext(AuthContext)?.user?.id ?? null;
}
